import React from 'react';
import { useSWRConfig } from 'swr';
import { ArrowClockwiseIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n/I18nProvider';
import { useStudentSync } from '@/lib/hooks/useStudentSync';
import { useGetStudentLastSync } from '@/lib/hooks/useGetStudentLastSync';
import { useToast } from '@/lib/hooks/use-toast';

type RefreshBarProps = {
  swrKey?: string | null;
  className?: string;
  onSynced?: () => void;
};

function RefreshBar({ swrKey, className = '', onSynced }: RefreshBarProps) {
  const { locale, t } = useI18n();
  const { mutate } = useSWRConfig();
  const { toast } = useToast();
  const { syncStudents, isSyncing } = useStudentSync();
  const { lastSyncedAt } = useGetStudentLastSync();

  const handleSync = async () => {
    if (isSyncing) return;

    try {
      // Trigger student synchronization endpoint (POST /api/students/sync)
      // useStudentSync internally revalidates last-sync status and student profile caches
      await syncStudents();

      if (swrKey) {
        await mutate(swrKey);
      }

      toast({
        title: t.refreshedSuccessfully,
        description: locale === 'ar' ? 'تم تحديث بيانات الطلاب بنجاح.' : 'Student profile data updated successfully.',
        variant: 'success',
      });

      if (onSynced) {
        onSynced();
      }
    } catch (err: any) {
      console.error('Student refresh failed:', err);

      const errorMessage = err.title ?? err.message ?? t.refreshFailed;

      toast({
        title: t.refreshFailed,
        description: errorMessage,
        variant: 'error',
      });
    }
  };

  const formatLastSyncDateTime = (date: Date | string | null) => {
    if (!date) return t.notRefreshed;

    let parsedDate: Date;
    if (typeof date === 'string') {
      const utcString = date.endsWith('Z') || date.includes('+') ? date : `${date}Z`;
      parsedDate = new Date(utcString);
    } else {
      parsedDate = new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      ));
    }

    if (isNaN(parsedDate.getTime())) return t.notRefreshed;

    return parsedDate.toLocaleString(locale === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Last Synced Timestamp Indicator (hidden on mobile, visible on sm+) */}
      <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full border border-border/50 flex-shrink-0">
        <span className="font-medium">{t.lastRefreshed}:</span>
        <span className="font-semibold text-foreground">{formatLastSyncDateTime(lastSyncedAt)}</span>
      </div>

      {/* Sync Button (icon-only on mobile, icon + label on sm+) */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleSync}
        disabled={isSyncing}
        title={isSyncing ? t.refreshingData : t.refreshData}
        className="h-9 sm:h-8 gap-1.5 px-2.5 sm:px-3 text-xs font-semibold bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 hover:text-primary transition-all shadow-sm flex-shrink-0"
      >
        <ArrowClockwiseIcon size={14} className={`${isSyncing ? 'animate-spin text-primary' : ''}`} />
        <span className="hidden sm:inline">{isSyncing ? t.refreshingData : t.refreshData}</span>
      </Button>
    </div>
  );
}

export default RefreshBar;
